/**
 * shims.ts — the ONE registry through which web code reaches the native app
 * shell (M111). This module imports NOTHING from Capacitor, so it costs the
 * web bundle zero bytes of native code: on the website every hook stays
 * undefined and callers use their web paths unchanged. Inside the app,
 * src/native/init.ts (a lazy chunk main.ts loads ONLY on native) fills the
 * hooks from the @capacitor/* plugins. Web code never imports @capacitor/*
 * directly — it reads this registry (the carLook-seam pattern).
 */
import type { StoreBuyOutcome } from '@/account/iapCredit';

/** The custom-scheme deep link OAuth/magic-link redirects land on. Registered
 *  in ios/App Info.plist (CFBundleURLSchemes) + the Android intent-filter, and
 *  it MUST be in the Supabase Auth "Redirect URLs" allowlist (runbook step). */
export const NATIVE_AUTH_CALLBACK = 'dribbleracer://auth-callback';

export interface NativeShims {
  /** 'ios' | 'android' once the native init has run; null on the web. */
  platform: 'ios' | 'android' | null;
  /** Real haptics (Haptics plugin). Falls back to navigator.vibrate on web. */
  haptic?: (kind: 'tick' | 'impact') => void;
  /** The native share sheet. Resolves 'dismissed' when the user closes it. */
  share?: (data: {
    title: string;
    text: string;
    url: string;
  }) => Promise<'shared' | 'dismissed'>;
  /** Native clipboard write (WKWebView's navigator.clipboard is unreliable). */
  copyText?: (text: string) => Promise<void>;
  /** Open a URL outside the game (privacy policy…) in the in-app browser. */
  openExternal?: (url: string) => void;
  /** Open an OAuth URL in the SYSTEM browser context (SFSafariViewController /
   *  Custom Tabs — the WebView-embedded flow is blocked by Google). */
  openAuthSession?: (url: string) => Promise<void>;
  /** Fully native Sign in with Apple (iOS only). Resolves the identity token
   *  + the RAW nonce for supabase signInWithIdToken; null = user cancelled. */
  appleSignIn?: () => Promise<{ identityToken: string; nonce: string } | null>;
  /** M156: a FRESH Sign in with Apple authorization code, asked for when an
   *  Apple Racer ID deletes itself (the server revokes the Apple tokens with
   *  it — Apple's account-deletion rule). 'cancelled' = the player closed the
   *  Apple sheet; null = Apple was unavailable (deletion goes ahead). */
  appleReauthCode?: () => Promise<{ code: string } | 'cancelled' | null>;
  /** Durable auth-session storage (Preferences → UserDefaults/SharedPrefs).
   *  WebView localStorage is OS-evictable; a boot-gated app must not lose its
   *  session to storage pressure. */
  authStorage?: {
    getItem(key: string): Promise<string | null>;
    setItem(key: string, value: string): Promise<void>;
    removeItem(key: string): Promise<void>;
  };
  /** Set by AccountService; called by native init when the auth deep link
   *  (NATIVE_AUTH_CALLBACK?code=…) opens the app. */
  onAuthCallback?: (url: string) => void;
  /** M133 — Shop Point packs bought through the STORE's own purchase sheet
   *  (StoreKit / Play Billing via RevenueCat). Present only inside the app
   *  AND only when a RevenueCat key is configured (config/iap.ts); absent =
   *  the native shop sells nothing. `userId` is the Racer ID — RevenueCat is
   *  logged in as it, so the webhook can credit the right wallet. */
  iap?: {
    /** The store's own localised price per pack id ("$2.99", "2,99 €"). A
     *  pack the store does not offer is simply missing from the result. */
    prices(packIds: readonly string[], userId: string): Promise<Record<string, string>>;
    /** Run the purchase sheet for one pack. M134: an outcome can be UNSURE
     *  (the payment may have been taken) — see account/iapCredit.ts. */
    buy(packId: string, userId: string): Promise<StoreBuyOutcome>;
  };
  /** M140 — crew-race push notifications (iOS: @capacitor/push-notifications
   *  → APNs). Present only inside the iOS app. Permission is asked in context
   *  (after joining a crew, behind our own soft-ask), never on launch. */
  push?: {
    permission(): Promise<'granted' | 'denied' | 'prompt'>;
    /** Ask if needed (the one-time iOS prompt) and register with APNs.
     *  Resolves the device token (hex), or null when denied / failed. */
    register(): Promise<string | null>;
  };
  /** M140: set by the crew layer — a push was TAPPED (app opened from it).
   *  `data` = the payload's custom fields (e.g. { crewRace: {...} }). */
  onPushTap?: (data: Record<string, unknown>) => void;
  /** M140: set by the crew layer — a push arrived while the app was OPEN
   *  (no system banner; the in-app pop-up shows instead). */
  onPushReceive?: (data: Record<string, unknown>) => void;
  /** M140: set by the crew layer — a universal link (https://dribbleracer.com
   *  /?crew=… / ?ref=…) opened the app. Only crew + friend codes are read
   *  from it; nothing in a link can steer anything else (the M100 rule). */
  onLinkOpen?: (url: string) => void;
}

export const nativeShims: NativeShims = { platform: null };

/** Are we inside the Capacitor app shell? Reads the bridge global the native
 *  runtime injects before any page script runs — zero imports, safe in node
 *  tests and on the web (both false). */
export function isNativeApp(): boolean {
  try {
    const w = globalThis as unknown as {
      Capacitor?: { isNativePlatform?: () => boolean };
    };
    return w.Capacitor?.isNativePlatform?.() === true;
  } catch {
    return false;
  }
}

/** Native init is async (a lazy chunk). Anything that must observe the filled
 *  registry (the Supabase client's storage adapter) awaits this; on the web it
 *  is already resolved. main.ts registers the real promise synchronously at
 *  boot, BEFORE Flow constructs, so there is no window where a native boot
 *  could build the client against the wrong storage. */
let readyPromise: Promise<void> = Promise.resolve();

export function setNativeReady(p: Promise<void>): void {
  // Never let a failed native init wedge callers — the shims just stay
  // unfilled and every consumer uses its web fallback.
  readyPromise = p.catch(() => undefined);
}

export function nativeReady(): Promise<void> {
  return readyPromise;
}
